import { useState, useEffect, useCallback } from 'react';
import { SK_DARK_MODE, SK_ACCESSIBILITY } from '../constants/storageKeys';
import type { AccessibilityPrefs } from '../NotificationPanel';

export function useAccessibility() {
  const [darkMode, setDarkModeValue] = useState(
    () => localStorage.getItem(SK_DARK_MODE) === 'true'
  );

  const [accessibilityPrefs, setAccessibilityPrefs] = useState<AccessibilityPrefs>(() => {
    const defaults: AccessibilityPrefs = { fontSize: 'default', highContrast: false, reducedMotion: false, reduceFlashing: false };
    try {
      const stored = localStorage.getItem(SK_ACCESSIBILITY);
      if (stored) return { ...defaults, ...JSON.parse(stored) };
    } catch { /* localStorage may be unavailable */ }
    return defaults;
  });

  const toggleDarkMode = useCallback(() => {
    setDarkModeValue(prev => {
      const newVal = !prev;
      localStorage.setItem(SK_DARK_MODE, String(newVal));
      if (newVal) {
        document.documentElement.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
      }
      return newVal;
    });
  }, []);

  const handleAccessibilityChange = useCallback((prefs: AccessibilityPrefs) => {
    setAccessibilityPrefs(prefs);
  }, []);

  // Apply accessibility class changes
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('large-text', accessibilityPrefs.fontSize === 'large');
    root.classList.toggle('high-contrast', accessibilityPrefs.highContrast);
    root.classList.toggle('reduced-motion', accessibilityPrefs.reducedMotion);
    root.classList.toggle('reduce-flashing', accessibilityPrefs.reduceFlashing);
    try {
      localStorage.setItem(SK_ACCESSIBILITY, JSON.stringify(accessibilityPrefs));
    } catch { /* localStorage may be unavailable */ }
  }, [accessibilityPrefs]);

  // Sync dark mode class on mount (inline script in index.html handles initial paint)
  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
  }, []);

  return {
    darkMode,
    toggleDarkMode,
    accessibilityPrefs,
    setAccessibilityPrefs,
    handleAccessibilityChange,
  };
}
